import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { fetchPhotoStats, fetchMetricsRate, fetchMetricsStats, fetchIngestionStats } from "../api";

/**
 * Custom hook for fetching and managing photo statistics with auto-refresh.
 *
 * @param {number} refreshMs - Refresh interval in milliseconds (0 to disable)
 * @returns {object} - Stats and metrics data with computed totals
 */
export function usePhotoStats(refreshMs = 10000) {
  const [stats, setStats] = useState(null);
  const [rate, setRate] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [ingestionStats, setIngestionStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const rateLabelRef = useRef("");

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      const [data, ingestion] = await Promise.all([
        fetchPhotoStats(),
        // Scope to pipeline events so overview counters stay aligned with
        // the grid (which already filters ingestion_mode=pipeline).
        fetchIngestionStats("pipeline").catch(() => null),
      ]);
      setStats(data);
      if (ingestion) setIngestionStats(ingestion);
    } catch (e) {
      console.error("Failed to load stats:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMetrics = useCallback(async () => {
    try {
      const [r, m] = await Promise.all([fetchMetricsRate(), fetchMetricsStats()]);
      setRate(r);
      setMetrics(m);

      // Calculate derived rate label
      const sumMs =
        (m?.avg_ms?.sati || 0) + (m?.avg_ms?.sentinel || 0) + (m?.avg_ms?.seraph || 0);
      const derivedRate = sumMs ? 60000 / sumMs : null;
      const display = derivedRate
        ? `${derivedRate.toFixed(2)} / min`
        : r?.per_min
          ? `${r.per_min.toFixed(2)} / min`
          : "";
      rateLabelRef.current = display;
    } catch (e) {
      // Ignore metrics errors
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadStats();
    loadMetrics();
  }, [loadStats, loadMetrics]);

  // Auto-refresh
  useEffect(() => {
    if (!refreshMs) return undefined;
    const statsId = setInterval(loadStats, refreshMs);
    const metricsId = setInterval(loadMetrics, refreshMs);
    return () => {
      clearInterval(statsId);
      clearInterval(metricsId);
    };
  }, [loadStats, loadMetrics, refreshMs]);

  /**
   * Compute totals from raw stats for UI display.
   * Maps V2 statuses to UI-friendly aggregations.
   */
  const totals = useMemo(() => {
    const byStatus = stats?.by_status || {};
    const total = stats?.total ?? 0;

    // V2: attention is a status now
    const attention = byStatus.attention || 0;
    const error = byStatus.error || 0;

    // V2: pending = review (photos ready for human review, all faces labeled)
    // to_tag = pending with unlabeled faces (needs tagging)
    const review = Math.max(0, (byStatus.pending || 0) - (stats?.to_tag || 0));
    const tagging = stats?.to_tag || 0;

    const done = byStatus.done || 0;
    const skipped = byStatus.skipped || 0;
    const ingested = byStatus.ingested || 0;
    const exported = byStatus.exported || 0;
    const recropping = byStatus.recropping || 0;

    // Pipeline processing: combine all pipeline states
    // (detecting/identifying are deprecated but may exist in historical data)
    const processing =
      (byStatus.processing || 0) +
      (byStatus.detecting || 0) +
      (byStatus.identifying || 0);

    // Combined done total includes exported
    const doneTotal = done + exported;

    return {
      total,
      review,
      tagging,
      done,
      doneTotal,
      attention,
      error,
      processing, // Combined: processing + detecting + identifying
      ingested,
      skipped,
      exported,
      recropping,
      byStatus,
    };
  }, [stats]);

  return {
    stats,
    totals,
    rate,
    metrics,
    ingestionStats,
    loading,
    rateLabel: rateLabelRef.current,
    refresh: loadStats,
    refreshMetrics: loadMetrics,
  };
}

export default usePhotoStats;
